/**
 * Staff PIN Management Tab - Admin Panel
 * Manages staff PINs for tablet login
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Key, 
  User, 
  Shield, 
  Check, 
  X, 
  Eye, 
  EyeOff,
  RefreshCw,
  Search,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Role badges with colors - ALL staff get PINs for device tracking
// Note: Manager and Supervisor roles have been combined into 'mgr-sup' as of June 2026
const ROLE_BADGES = {
  owner: { label: 'Owner', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  'mgr-sup': { label: 'Mgr-Sup', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  manager: { label: 'Manager (Legacy)', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  supervisor: { label: 'Supervisor (Legacy)', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' },
  cashier: { label: 'Cashier', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  server: { label: 'Server', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  barista: { label: 'Barista', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  kitchen: { label: 'Kitchen', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  juicer: { label: 'Juicer', color: 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-300' },
  counter: { label: 'Counter', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300' },
  dishwasher: { label: 'Dishwasher', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' },
  security: { label: 'Security', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' },
  accountant: { label: 'Accountant', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
  staff: { label: 'Staff', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' }
};

export default function StaffPinManagerTab() {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [confirmPinInput, setConfirmPinInput] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchStaff = useCallback(async () => {
    try {
      const response = await api.get('/device-sessions/staff-pins');
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleSetPin = async (staffId) => {
    // Validate PIN
    if (pinInput.length !== 6 || !/^\d{6}$/.test(pinInput)) {
      toast.error('PIN must be exactly 6 digits');
      return;
    }
    
    if (pinInput !== confirmPinInput) {
      toast.error('PINs do not match');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/device-sessions/staff-pins/${staffId}`, { pin: pinInput });
      toast.success('PIN set successfully');
      setSelectedStaff(null);
      setPinInput('');
      setConfirmPinInput('');
      await fetchStaff();
    } catch (error) {
      console.error('Error setting PIN:', error);
      toast.error(error.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePin = async (staffId) => {
    if (!confirm('Are you sure you want to remove this PIN?')) return;
    
    try {
      await api.delete(`/device-sessions/staff-pins/${staffId}`);
      toast.success('PIN removed');
      await fetchStaff();
    } catch (error) {
      console.error('Error removing PIN:', error);
      toast.error('Failed to remove PIN');
    }
  };

  // Filter staff by search query
  const filteredStaff = staffList.filter(staff => {
    const query = searchQuery.toLowerCase();
    return (
      staff.name?.toLowerCase().includes(query) ||
      staff.email?.toLowerCase().includes(query) ||
      staff.role?.toLowerCase().includes(query)
    );
  });

  // Group by whether they have a PIN
  const staffWithPin = filteredStaff.filter(s => s.has_pin);
  const staffWithoutPin = filteredStaff.filter(s => !s.has_pin);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Staff PIN Management</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Assign 6-digit PINs for tablet login
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, email, or role..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
              <Key className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{staffWithPin.length}</p>
              <p className="text-xs text-green-600 dark:text-green-400">With PIN Assigned</p>
            </div>
          </div>
        </div>
        
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{staffWithoutPin.length}</p>
              <p className="text-xs text-amber-600 dark:text-amber-400">No PIN Assigned</p>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Without PIN - Priority */}
      {staffWithoutPin.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Staff Without PIN ({staffWithoutPin.length})
          </h3>
          <div className="grid gap-3">
            {staffWithoutPin.map(staff => (
              <StaffPinCard
                key={staff.id}
                staff={staff}
                onSetPin={() => {
                  setSelectedStaff(staff);
                  setPinInput('');
                  setConfirmPinInput('');
                }}
                onRemovePin={handleRemovePin}
              />
            ))}
          </div>
        </div>
      )}

      {/* Staff With PIN */}
      {staffWithPin.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-green-700 dark:text-green-300 flex items-center gap-2">
            <Check className="w-4 h-4" />
            Staff With PIN ({staffWithPin.length})
          </h3>
          <div className="grid gap-3">
            {staffWithPin.map(staff => (
              <StaffPinCard
                key={staff.id}
                staff={staff}
                onSetPin={() => {
                  setSelectedStaff(staff);
                  setPinInput('');
                  setConfirmPinInput('');
                }}
                onRemovePin={handleRemovePin}
              />
            ))}
          </div>
        </div>
      )}

      {/* PIN Entry Modal */}
      {selectedStaff && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {selectedStaff.has_pin ? 'Update PIN' : 'Set PIN'} for {selectedStaff.name || selectedStaff.email}
              </h3>
              <button
                onClick={() => {
                  setSelectedStaff(null);
                  setPinInput('');
                  setConfirmPinInput('');
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  6-Digit PIN
                </label>
                <div className="relative">
                  <input
                    type={showPin ? 'text' : 'password'}
                    value={pinInput}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setPinInput(val);
                    }}
                    placeholder="Enter 6 digits"
                    maxLength={6}
                    className="w-full px-4 py-3 text-2xl font-mono text-center tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPin ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm PIN
                </label>
                <input
                  type={showPin ? 'text' : 'password'}
                  value={confirmPinInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setConfirmPinInput(val);
                  }}
                  placeholder="Confirm 6 digits"
                  maxLength={6}
                  className="w-full px-4 py-3 text-2xl font-mono text-center tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              {pinInput.length === 6 && confirmPinInput.length === 6 && pinInput !== confirmPinInput && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <X className="w-4 h-4" />
                  PINs do not match
                </p>
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setSelectedStaff(null);
                  setPinInput('');
                  setConfirmPinInput('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSetPin(selectedStaff.id)}
                disabled={pinInput.length !== 6 || pinInput !== confirmPinInput || saving}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Key className="w-4 h-4" />
                )}
                {selectedStaff.has_pin ? 'Update PIN' : 'Set PIN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StaffPinCard({ staff, onSetPin, onRemovePin }) {
  const roleBadge = ROLE_BADGES[staff.role] || ROLE_BADGES.staff;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${staff.has_pin ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
          {staff.has_pin ? (
            <Key className="w-5 h-5 text-green-600 dark:text-green-400" />
          ) : (
            <User className="w-5 h-5 text-gray-400" />
          )}
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{staff.name || 'Unnamed Staff'}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{staff.email}</p>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleBadge.color}`}>
          {roleBadge.label}
        </span>
        
        {staff.has_pin ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <Check className="w-3 h-3" />
              PIN Set
            </span>
            <button
              onClick={onSetPin}
              className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            >
              Change
            </button>
            <button
              onClick={() => onRemovePin(staff.id)}
              className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              Remove
            </button>
          </div>
        ) : (
          <button
            onClick={onSetPin}
            className="px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Set PIN
          </button>
        )}
      </div>
    </div>
  );
}
